import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Container,
  Stack,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Avatar,
  TablePagination,
  Snackbar,
  Alert as MuiAlert,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
 DialogActions,
 DialogContentText,
 FormControlLabel,
 Switch
} from '@mui/material';
import {
 People as PeopleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Engineering as InspectorIcon,
  Visibility as ViewerIcon,
  Business as ManagerIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
 Warning as WarningIcon,
 RestoreFromTrash as RestoreFromTrashIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import authService from '../../services/auth/authService';
import { disciplinesApi } from '../../services/api';
import CreateUserModal from './CreateUserModal';
import EditUserModal from './EditUserModal';
import AccessRequestReviewModal from './AccessRequestReviewModal';
import TeamManagementPanel from './TeamManagementPanel';
import LoadingSpinner from '../LoadingSpinner';

const UserListPage = () => {
  const { user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
 const [permissionScopeFilter, setPermissionScopeFilter] = useState('');
 const [showInactive, setShowInactive] = useState(false);
 const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Total number of available disciplines (asset categories), used to detect "All Disciplines"
  const [totalDisciplines, setTotalDisciplines] = useState(0);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar states (same as MapView)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info'); // 'info', 'warning', 'error', 'success'

  // Access request states
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingRequestsLoading, setPendingRequestsLoading] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(searchParams.get('tab') === 'pending');
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Fetch users on component mount
  useEffect(() => {
   fetchUsers();
   fetchPendingRequests();
   fetchTotalDisciplines();
 }, [showInactive]);

 // Apply filters when search term or filters change
  useEffect(() => {
    applyFilters();
  }, [users, searchTerm, roleFilter, permissionScopeFilter]);

  const fetchUsers = async () => {
   try {
     setLoading(true);
     setError(null);
     
     const response = await authService.getUsers(showInactive ? false : true);
     if (response.success) {
       setUsers(response.users || []);
     } else {
        setError(response.error || 'Failed to fetch users');
        // Show error notification
        setSnackbarMessage('Failed to load users. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
      // Show error notification
      setSnackbarMessage('Failed to load users. Please check your connection and try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      setPendingRequestsLoading(true);
      const response = await authService.getAccessRequests('pending');
      if (response.success) {
        setPendingRequests(response.requests || []);
      }
    } catch (err) {
      console.error('Error fetching pending requests:', err);
    } finally {
      setPendingRequestsLoading(false);
    }
  };

  const fetchTotalDisciplines = async () => {
    try {
      const response = await disciplinesApi.getDisciplines();
      setTotalDisciplines(Array.isArray(response.data) ? response.data.length : 0);
    } catch (err) {
      console.error('Error fetching disciplines:', err);
      // Leave totalDisciplines at 0 so we never falsely show "All Disciplines"
    }
  };

  const handleRequestProcessed = () => {
    fetchPendingRequests();
    fetchUsers();
    setSnackbarMessage('Access request processed successfully');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const applyFilters = () => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.organization_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply role filter
    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Apply permission scope filter
    if (permissionScopeFilter) {
      filtered = filtered.filter(user => user.permission_scope === permissionScopeFilter);
    }

    setFilteredUsers(filtered);
    setPage(0); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setPermissionScopeFilter('');
  };

  const handleCreateUser = () => {
    setCreateModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleUserCreated = (newUser) => {
    setUsers(prev => [...prev, newUser]);
    // Show success notification
    setSnackbarMessage(`User "${newUser.full_name || newUser.username}" created successfully!`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleUserUpdated = (updatedUser) => {
    setUsers(prev => prev.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    ));
    // Show success notification
    setSnackbarMessage(`User "${updatedUser.full_name || updatedUser.username}" updated successfully!`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(true);
      const response = await authService.deleteUser(userToDelete.id);
      
      if (response.success) {
        // Remove user from list
        setUsers(prev => prev.filter(user => user.id !== userToDelete.id));
        
        // Show success notification
        setSnackbarMessage(`User "${userToDelete.full_name || userToDelete.username}" has been deleted successfully.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Close dialog
        setDeleteDialogOpen(false);
        setUserToDelete(null);
      } else {
        setSnackbarMessage(response.error || 'Failed to delete user');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbarMessage('Failed to delete user. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
   }
 };

 const handleReactivateUser = async (user) => {
   try {
     const response = await authService.reactivateUser(user.id);
     if (response.success) {
       setSnackbarMessage(`User "${user.full_name || user.username}" has been reactivated.`);
       setSnackbarSeverity('success');
       setSnackbarOpen(true);
       fetchUsers(); // Refresh the user list
     } else {
       setSnackbarMessage(response.error || 'Failed to reactivate user');
       setSnackbarSeverity('error');
       setSnackbarOpen(true);
     }
   } catch (error) {
     console.error('Error reactivating user:', error);
     setSnackbarMessage('Failed to reactivate user. Please try again.');
     setSnackbarSeverity('error');
     setSnackbarOpen(true);
   }
 };

 const canDeleteUser = (user) => {
   // Managers can delete users within their organization (except themselves)
   if (currentUser?.role === 'manager') {
      return user.id !== currentUser.id && 
             user.organization_id === currentUser.organization_id &&
             ['inspector', 'viewer'].includes(user.role);
    }
    
    // System admins can delete any user except other admins
    if (currentUser?.role === 'admin' && currentUser?.organizationId === null) {
      return user.role !== 'admin' || user.id === currentUser.id;
    }
    
    return false;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <AdminIcon />;
      case 'manager': return <ManagerIcon />;
      case 'inspector': return <InspectorIcon />;
      case 'viewer': return <ViewerIcon />;
      default: return <PersonIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'primary';
      case 'inspector': return 'warning';
      case 'viewer': return 'info';
      default: return 'default';
    }
  };

  const getPermissionScopeColor = (scope) => {
    return scope === 'organization' ? 'success' : 'secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canManageUsers = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  // Early return for loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading users...
        </Typography>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchUsers}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  // Calculate pagination
  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Snackbar close handler (same as MapView)
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <PeopleIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users, roles, and permissions for your organization
            {currentUser?.organizationName && ` - ${currentUser.organizationName}`}
          </Typography>
        </Box>
      </Stack>

      {/* Pending Access Requests Section */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2, border: '1px solid', borderColor: pendingRequests.length > 0 ? 'warning.main' : 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Pending Access Requests
            </Typography>
            {pendingRequests.length > 0 ? (
              <Chip label={pendingRequests.length} size="small" color="warning" />
            ) : (
              <Chip label="0" size="small" variant="outlined" />
            )}
          </Stack>
          {pendingRequests.length > 0 && (
            <Button
              size="small"
              onClick={() => setShowPendingRequests(!showPendingRequests)}
            >
              {showPendingRequests ? 'Hide' : 'Show'}
            </Button>
          )}
        </Stack>

        {pendingRequests.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No pending access requests at the moment. When a new user logs in via SSO for the first time, their request will appear here for your review.
          </Typography>
        ) : showPendingRequests && (
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Disciplines</TableCell>
                  <TableCell>Locations</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRequests.map((req) => (
                  <TableRow key={req.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {req.full_name || req.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{req.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(req.requested_disciplines || []).map((d, i) => (
                          <Chip key={i} label={d.name} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {(req.requested_locations || []).length} location(s)
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(req.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setSelectedRequest(req);
                          setReviewModalOpen(true);
                        }}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Teams Management */}
      {canManageUsers && (
        <TeamManagementPanel onMembershipChange={fetchUsers} />
      )}

      {/* Search and Filter Controls */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        {/* Search Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
            Search & Filters
          </Typography>
          <TextField
            placeholder="Search by name, username, email, or organization..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: searchTerm && (
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon />
                </IconButton>
              )
            }}
            sx={{ 
              width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'grey.50',
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
                '&.Mui-focused': {
                  backgroundColor: 'background.paper',
                }
              }
            }}
          />
        </Box>

        {/* Filters and Actions Section */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={{ xs: 2, sm: 3 }} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          {/* Filter Controls */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flex: 1 }}>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 140 } }}>
              <InputLabel size="small">Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                size="small"
                onChange={(e) => setRoleFilter(e.target.value)}
                sx={{ 
                  borderRadius: 2,
                  backgroundColor: 'grey.50',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  }
                }}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="inspector">Inspector</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl sx={{ minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel size="small">Permission Scope</InputLabel>
              <Select
                value={permissionScopeFilter}
                label="Permission Scope"
                size="small"
                onChange={(e) => setPermissionScopeFilter(e.target.value)}
                sx={{ 
                  borderRadius: 2,
                  backgroundColor: 'grey.50',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  }
                }}
              >
                <MenuItem value="">All Scopes</MenuItem>
                <MenuItem value="organization">Organization</MenuItem>
                <MenuItem value="network_section">Network/Section</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Action Buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ minWidth: 'fit-content' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!searchTerm && !roleFilter && !permissionScopeFilter}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: { xs: '100%', sm: 'auto' },
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'grey.400',
                  backgroundColor: 'grey.50',
                }
              }}
            >
              Clear Filters
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchUsers();
                // Show refresh notification
                setSnackbarMessage('User list refreshed successfully!');
                setSnackbarSeverity('info');
                setSnackbarOpen(true);
              }}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: { xs: '100%', sm: 'auto' },
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50',
                }
              }}
            >
              Refresh
            </Button>
            {canManageUsers && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleCreateUser}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: { xs: '100%', sm: 'auto' },
                  background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                  boxShadow: '0 3px 10px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                Add User
              </Button>
            )}
          </Stack>
         </Stack>
         <FormControlLabel
           control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
           label="Show Inactive Users"
         />
       </Paper>
 
       {/* Users Table */}
      <Paper sx={{ p: 3, borderRadius: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <PeopleIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Users ({filteredUsers.length})
          </Typography>
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Permission Scope</TableCell>
                <TableCell>Email Group</TableCell>
                <TableCell>Team</TableCell>
                <TableCell>Disciplines</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Email Status</TableCell>
                <TableCell>Last Login</TableCell>
                {canManageUsers && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageUsers ? 10 : 9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      {users.length === 0 ? 'No users found' : 'No users match your filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: `${getRoleColor(user.role)}.main` }}>
                          {getRoleIcon(user.role)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {user.full_name || user.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            @{user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(user.role)}
                        label={user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          user.permission_scope === 'organization' 
                            ? 'Organization' 
                            : 'Network/Section'
                        }
                        color={getPermissionScopeColor(user.permission_scope)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.group_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.teams?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 220 }}>
                          {user.teams.map((t) => (
                            <Chip key={t.id} label={t.name} size="small" variant="outlined" />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.disciplines?.length > 0 ? (
                        <Box sx={{ maxWidth: 180 }}>
                          {(() => {
                            // Check if user has all available disciplines (asset categories), fetched dynamically.
                            // Guard on totalDisciplines > 0 so an unloaded/failed fetch never falsely shows "All Disciplines".
                            const hasAllDisciplines = totalDisciplines > 0 && user.disciplines.length >= totalDisciplines;
                            
                            if (hasAllDisciplines) {
                              return (
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                        All Assigned Disciplines:
                                      </Typography>
                                      {user.disciplines.map(d => (
                                        <Typography key={d.id} variant="body2" sx={{ py: 0.25 }}>
                                          • {d.name}
                                        </Typography>
                                      ))}
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Chip 
                                    label="All Disciplines" 
                                    size="small" 
                                    variant="filled"
                                    color="success"
                                    sx={{ 
                                      height: 24,
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      cursor: 'pointer'
                                    }}
                                  />
                                </Tooltip>
                              );
                            } else if (user.disciplines.length === 1) {
                              return (
                                <Chip 
                                  label={user.disciplines[0].name} 
                                  size="small" 
                                  variant="outlined"
                                  color="primary"
                                  sx={{ 
                                    height: 24,
                                    fontSize: '0.75rem',
                                    fontWeight: 500
                                  }}
                                />
                              );
                            } else {
                              // For 2+ disciplines (but not all), show count with tooltip
                              return (
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                        Assigned Disciplines ({user.disciplines.length}):
                                      </Typography>
                                      {user.disciplines.map(d => (
                                        <Typography key={d.id} variant="body2" sx={{ py: 0.25 }}>
                                          • {d.name}
                                        </Typography>
                                      ))}
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Chip 
                                    label={`${user.disciplines.length} Disciplines`} 
                                    size="small" 
                                    variant="outlined"
                                    color="primary"
                                    sx={{ 
                                      height: 24,
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      cursor: 'pointer'
                                    }}
                                  />
                                </Tooltip>
                              );
                            }
                          })()}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not Assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.organization_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.email_verified ? 'Verified' : 'Pending'}
                        color={user.email_verified ? 'success' : 'warning'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.last_login)}
                      </Typography>
                    </TableCell>
                    {canManageUsers && (
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(user)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {showInactive ? (
                           <Tooltip title="Reactivate User">
                             <IconButton
                                size="small"
                                onClick={() => handleReactivateUser(user)}
                                color="success"
                              >
                                <RestoreFromTrashIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            canDeleteUser(user) && (
                              <Tooltip title="Delete User">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteUser(user)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                        </Stack>
                      </TableCell>
                    )}
                 </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Paper>

      {/* Modals */}
      <CreateUserModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onUserCreated={handleUserCreated}
      />

      {selectedUser && (
        <EditUserModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onUserUpdated={handleUserUpdated}
        />
      )}

      {/* Access Request Review Modal */}
      <AccessRequestReviewModal
        open={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onRequestProcessed={handleRequestProcessed}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <WarningIcon color="error" />
            <Typography variant="h6">Delete User Account</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the user account for{' '}
            <strong>{userToDelete?.full_name || userToDelete?.username}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
            <strong>Warning:</strong> This action cannot be undone. The user will lose access 
            to the system immediately and all their data associations will be removed.
          </DialogContentText>
          {userToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>User Details:</strong>
              </Typography>
              <Typography variant="body2">Name: {userToDelete.full_name || 'N/A'}</Typography>
              <Typography variant="body2">Username: {userToDelete.username}</Typography>
              <Typography variant="body2">Email: {userToDelete.email}</Typography>
              <Typography variant="body2">Role: {userToDelete.role}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteUser}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Snackbar for User Management Notifications (same style as MapView) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000} // Increased duration for better readability
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Changed position to top-center
        TransitionComponent={Zoom} // Added zoom transition effect
        sx={{
          marginTop: 2, // Add some margin from the top
        }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          elevation={6}
          sx={{
            width: '100%',
            minWidth: '300px', // Minimum width for better visibility
            fontWeight: 500,
            borderRadius: '8px',
            boxShadow: '0 8px 16px rgba(0,0,0,0.15)', // Enhanced shadow
            padding: '10px 16px', // More comfortable padding
            '.MuiAlert-icon': {
              fontSize: '1.4rem', // Larger icon
              marginRight: '12px', // More space between icon and text
            },
            '.MuiAlert-message': {
              fontSize: '0.95rem', // Slightly larger text
              display: 'flex',
              alignItems: 'center',
              fontFamily: (theme) => theme.typography.fontFamily, // Ensure consistent font
            },
            '.MuiAlert-action': {
              paddingTop: '4px', // Align close button better
            },
            border: (theme) => `1px solid ${theme.palette[snackbarSeverity]?.light || theme.palette.info.light}`, // Subtle border
            backdropFilter: 'blur(8px)', // Modern blur effect
          }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default UserListPage; 